import { Button, Card, Checkbox, Col, Form, message, Row } from "antd";
import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { TagsInput } from "react-tag-input-component";
import cmaApi from "../../../../../api/cmaApi";
import InputField from "../../../../../components/InputField";
import InputNumberWrap from "../../../../../components/InputNumber";
import Loading from "../../../../../components/Loading";
import SelectField from "../../../../../components/SelectField";
import TextAreaField from "../../../../../components/TextAreaField";
import TopContent from "../../../../../components/TopContent";
import PageWrap from "../../../../../layout/PageWrap";
import { appRoutes } from "../../../../../routes/routes";
import { formItemLayoutSm } from "../../../../../utils/constants";
import { messages } from "../../../../../utils/messages";

const iconOptions = [
  { key: "clipboard", value: "clipboard" },
  { key: "building", value: "building" },
  { key: "pieChart", value: "pieChart" },
  { key: "message", value: "message" },
];

interface FeaturesFormProps {
  type: number;
}

const FeaturesForm: FC<FeaturesFormProps> = ({ type }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const [points, setPoints] = useState<string[]>([]);
  const [pointsEn, setPointsEn] = useState<string[]>([]);

  const { data, isLoading } = useQuery(
    ["cma_landing_features", id],
    () => cmaApi.getLandingFeatureDetails(id),
    { enabled: type === 2 }
  );

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (data?.data?.points_vi) {
      setPoints(data.data.points_vi);
    }
    if (data?.data?.points_en) {
      setPointsEn(data.data.points_en);
    }
  }, [data]);

  const mutationEdit = useMutation(cmaApi.editLandingFeature, {
    onSuccess: async (res: any) => {
      if (res.statusCode === 200) {
        message.success(messages.updateSuccess);
        await queryClient.invalidateQueries("cma_landing_features");
        navigate(appRoutes.CMA.LANDING.FEATURES.LIST);
      } else message.error(res?.data?.message ?? messages.error);
    },
    onError: (err: any) => {
      message.error(err?.data?.message ?? messages.error);
    },
  });

  const mutationAdd = useMutation(cmaApi.addLandingFeature, {
    onSuccess: async (res: any) => {
      if (res.statusCode === 200) {
        message.success(messages.addSuccess);
        await queryClient.invalidateQueries("cma_landing_features");
        navigate(appRoutes.CMA.LANDING.FEATURES.LIST);
      } else message.error(res?.data?.message ?? messages.error);
    },
    onError: (err: any) => {
      message.error(err?.data?.message ?? messages.error);
    },
  });

  const onSubmit = (dt: any) => {
    const payload = {
      ...dt,
      points_vi: points,
      points_en: pointsEn,
      hienthi:
        dt?.hienthi ??
        (type === 2 ? data?.data?.hienthi ?? false : false),
    };
    if (type === 2) mutationEdit.mutate({ id, data: payload });
    if (type === 1) mutationAdd.mutate(payload);
  };

  return (
    <PageWrap
      title="CMA · Tính năng"
      content={
        <>
          <TopContent
            content={
              <Row>
                <Col>
                  <b>CMA · Tính năng</b>
                </Col>
              </Row>
            }
          />
          <div className="content">
            {isLoading ? (
              <Loading />
            ) : (
              <Card bordered={false}>
                <Form {...formItemLayoutSm} className="slider-form">
                  <SelectField
                    label="Icon"
                    name="icon"
                    control={control}
                    options={iconOptions}
                    defaultValue={data?.data?.icon}
                    errorMsg={errors?.icon?.message}
                    rules={{ required: "Vui lòng chọn icon" }}
                    placeholder="Chọn icon"
                  />
                  <InputField
                    label="Href"
                    name="href"
                    control={control}
                    setValue={setValue}
                    defaultValue={data?.data?.href}
                    errorMsg={errors?.href?.message}
                    rules={{ required: "Vui lòng nhập href" }}
                  />
                  <InputField
                    label="Tiêu đề (VI)"
                    name="ten_vi"
                    control={control}
                    setValue={setValue}
                    defaultValue={data?.data?.ten_vi}
                    errorMsg={errors?.ten_vi?.message}
                    rules={{ required: "Vui lòng nhập tiêu đề" }}
                  />
                  <InputField
                    label="Tiêu đề (EN)"
                    name="ten_en"
                    control={control}
                    setValue={setValue}
                    defaultValue={data?.data?.ten_en}
                  />
                  <TextAreaField
                    label="Mô tả (VI)"
                    name="mota_vi"
                    control={control}
                    defaultValue={data?.data?.mota_vi}
                    errorMsg={errors?.mota_vi?.message}
                    rules={{ required: "Vui lòng nhập mô tả" }}
                  />
                  <TextAreaField
                    label="Mô tả (EN)"
                    name="mota_en"
                    control={control}
                    defaultValue={data?.data?.mota_en}
                  />
                  <Form.Item label="Points (VI)" colon={false}>
                    <TagsInput
                      value={points}
                      onChange={(e: any) => setPoints(e)}
                      placeHolder="Thêm điểm nổi bật"
                    />
                  </Form.Item>
                  <Form.Item label="Points (EN)" colon={false}>
                    <TagsInput
                      value={pointsEn}
                      onChange={(e: any) => setPointsEn(e)}
                      placeHolder="Add highlights (EN)"
                    />
                  </Form.Item>
                  <InputNumberWrap
                    label="STT"
                    name="stt"
                    control={control}
                    defaultValue={data?.data?.stt ?? 1}
                    onBlur={(e: any) => {
                      if (e.target.value === "") setValue("stt", 1);
                    }}
                    min={1}
                  />
                  <Form.Item className="checkbox">
                    <Row>
                      <Col xs={24} sm={20}>
                        <Checkbox
                          defaultChecked={data?.data?.hienthi}
                          name="hienthi"
                          onClick={(e: any) => {
                            setValue("hienthi", e.target.checked);
                          }}
                        />
                        &nbsp;Hiển thị
                      </Col>
                    </Row>
                  </Form.Item>
                  <Row justify="start" gutter={[10, 0]}>
                    <Col>
                      <Button
                        type="primary"
                        danger
                        onClick={handleSubmit(onSubmit)}
                      >
                        Lưu
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        onClick={() =>
                          navigate(appRoutes.CMA.LANDING.FEATURES.LIST)
                        }
                      >
                        Hủy
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Card>
            )}
          </div>
        </>
      }
    />
  );
};

export default FeaturesForm;
